import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, User, Zap, ArrowRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { getFunnelContext, mergeFunnelContext, trackEvent, trackPageView } from '@/lib/analytics';
import { activatePreviewMode } from '@/lib/preview-mode';
import { safeRedirectPath } from '@/lib/safe-redirect';
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton';

const benefits = [
  'Watermarked preview access to get your first win',
  'No credit card required',
  'Unlock export with launch packs and campaign workflows when you are ready',
];

const planNames: Record<string, string> = {
  launch: 'Launch Pack (one-time)',
  starter: 'Starter plan (monthly)',
  pro: 'Growth plan (monthly)',
  enterprise: 'Scale plan (monthly)',
};

export default function SignupPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoFeedback, setPromoFeedback] = useState<{
    status: 'applied' | 'invalid' | 'already_used';
    message: string;
  } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const postSignupDestinationRef = useRef('/');
  const { signup, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const fireCompleteRegistration = () => {
    const tracker = (window as Window & { ttq?: { track?: (name: string) => void } }).ttq;
    if (tracker && typeof tracker.track === 'function') {
      tracker.track('CompleteRegistration');
    }
  };

  useEffect(() => {
    const plan = searchParams.get('plan');
    if (plan && planNames[plan]) {
      setSelectedPlan(plan);
    }
  }, [searchParams]);

  // Read redirect_to from query params to preserve navigation intent. Only a
  // path on this site; PublicRoute (App.tsx) applies the same rule.
  const redirectTo = safeRedirectPath(searchParams.get('redirect_to'));
  useEffect(() => {
    if (redirectTo) {
      postSignupDestinationRef.current = redirectTo;
    }
  }, [redirectTo]);

  useEffect(() => {
    const selected = searchParams.get('plan');
    mergeFunnelContext({
      funnelSource: selected ? 'signup_plan_entry' : 'signup_direct',
      funnelEntryPage: 'signup',
      funnelSelectedPlan: selected || undefined,
      funnelOfferType: selected === 'launch' ? 'launch_pack' : selected ? 'subscription_plan' : 'free_preview',
      funnelBillingModel:
        selected === 'launch' ? 'one_time' : selected ? 'monthly' : 'free',
      funnelOfferName: selected && planNames[selected] ? planNames[selected] : 'Free Preview',
    });
    trackPageView('signup', { selectedPlan: selected || null });
  }, [searchParams]);

  useEffect(() => {
    if (user) {
      navigate(postSignupDestinationRef.current);
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[SignupPage] Submit clicked');

    if (password.length < 6) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 6 characters.',
        variant: 'destructive',
      });
      return;
    }

    trackEvent('signup_started', {
      selectedPlan: selectedPlan || null,
      hasPromoCode: Boolean(promoCode.trim()),
    });

    setIsLoading(true);
    setPromoFeedback(null);

    const result = await signup(email, password, name, promoCode);

    if (result.success) {
      console.log('[SignupPage] Signup successful, navigating to home');
      const funnelContext = getFunnelContext();
      // If a redirect_to was provided (e.g., from landing photobook CTA), use it
      if (!redirectTo) {
        const isPreviewFlow =
          !selectedPlan ||
          funnelContext.funnelOfferType === 'free_preview';
        if (isPreviewFlow) {
          activatePreviewMode(typeof funnelContext.funnelSource === 'string' ? funnelContext.funnelSource : 'signup_free_preview');
          postSignupDestinationRef.current = '/chopshop';
        } else {
          postSignupDestinationRef.current = '/';
        }
      }
      toast({
        title: 'Account created!',
        // Say where the confirmation link went, only when one really did.
        description: result.verificationEmailSent
          ? t('signup.verificationSent', { email: email.trim() })
          : 'Welcome to ChopShop.',
      });
      if (result.promo && result.promo.message) {
        if (
          result.promo.status === 'applied' ||
          result.promo.status === 'invalid' ||
          result.promo.status === 'already_used'
        ) {
          setPromoFeedback({
            status: result.promo.status,
            message: result.promo.message,
          });
        }
      }
      fireCompleteRegistration();
    } else {
      console.warn('[SignupPage] Signup failed', { error: result.error });
      toast({
        title: 'Signup failed',
        description: result.error || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden px-4 py-12">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-hero">
        <motion.div
          className="absolute top-1/4 left-1/3 w-80 h-80 rounded-full bg-primary/20 blur-[100px]"
          animate={{
            x: [0, -30, 0],
            y: [0, 20, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/3 w-80 h-80 rounded-full bg-accent/20 blur-[100px]"
          animate={{
            x: [0, 30, 0],
            y: [0, -20, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-glow">
            <Zap className="w-6 h-6 text-primary-foreground" />
          </div>
          <span className="text-2xl font-bold font-display">
            <span className="gradient-text">Chop</span>Shop
          </span>
        </Link>

        {/* Card */}
        <div className="glass-card-elevated p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold mb-2">{t('signup.createAccount')}</h1>
            <p className="text-muted-foreground">{t('signup.startCreating')}</p>
            {selectedPlan && (
              <div className="mt-3 px-4 py-2 rounded-lg bg-primary/10 border border-primary/20">
                <p className="text-sm font-medium text-primary">
                  {t('signup.selected')}: {planNames[selectedPlan]}
                </p>
              </div>
            )}
          </div>

          {/* Benefits */}
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {benefits.map((benefit) => (
              <div
                key={benefit}
                className="flex items-center gap-1.5 text-xs text-muted-foreground"
              >
                <Check className="w-3.5 h-3.5 text-primary" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>

          {/* Google above the form (2026-09-25). Its session reaches `user`
              exactly like a password sign-up's, so the redirect_to effect
              above (and PublicRoute) take it from there. */}
          <GoogleSignInButton source="signup_page" />
          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <span aria-hidden="true" className="h-px flex-1 bg-border" />
            {t('auth.orDivider')}
            <span aria-hidden="true" className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('signup.nameOptional')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder={t('signup.yourName')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('signup.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t('signup.youExample')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('signup.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('signup.min6Chars')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="rounded-lg border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-muted-foreground">
              <span className="font-semibold text-primary">{t('signup.newUserPromo')}</span>{' '}
              {t('signup.promoCodeText')}
            </div>

            <div className="space-y-2">
              <Label htmlFor="promoCode">{t('signup.promoCodeOptional')}</Label>
              <Input
                id="promoCode"
                type="text"
                placeholder={t('signup.enterPromoCode')}
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
              />
              {promoFeedback && (
                <p
                  className={
                    promoFeedback.status === 'applied'
                      ? 'text-sm text-primary'
                      : 'text-sm text-destructive'
                  }
                >
                  {promoFeedback.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              variant="hero"
              className="w-full"
              disabled={isLoading}
              onClick={fireCompleteRegistration}
            >
              {isLoading ? t('signup.creatingAccount') : t('signup.createAccountBtn')}
              {!isLoading && <ArrowRight className="w-4 h-4 ml-1" />}
            </Button>
          </form>

          <p className="mt-4 text-xs text-center text-muted-foreground">
            {t('signup.bySigningUp')}{' '}
            <Link to="/terms" className="text-primary hover:underline">{t('signup.terms')}</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-primary hover:underline">{t('signup.privacyPolicy')}</Link>
          </p>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground text-sm">
              {t('signup.alreadyHaveAccount')}{' '}
              <Link
                to={redirectTo ? `/login?redirect_to=${encodeURIComponent(redirectTo)}` : '/login'}
                className="text-primary font-semibold hover:underline"
              >
                {t('signup.signIn')}
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
